import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useToaster } from '../components/common/Toaster';
import {
  Card,
  CardContent,
  CardIconHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  FolderKanban,
  Plus,
  ArrowLeft,
  ListTodo,
  Clock,
  CheckCircle2,
  Eye,
  AlertTriangle,
} from 'lucide-react';
import { taskService } from '../Services/taskService';
import KanbanBoard from '../components/Tasks/KanbanBoard';
import CreateTaskDialog from '../components/Tasks/CreateTaskDialog';
import TaskDetailSheet from '../components/Tasks/TaskDetailSheet';
import type {
  ProjectDto,
  ProjectStatisticsDto,
  ProjectMemberDto,
  TaskItemDto,
  CreateTaskItemDto,
} from '../types/tasks';

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { notify } = useToaster();

  const [project, setProject] = useState<ProjectDto | null>(null);
  const [statistics, setStatistics] = useState<ProjectStatisticsDto | null>(null);
  const [members, setMembers] = useState<ProjectMemberDto[]>([]);
  const [tasks, setTasks] = useState<TaskItemDto[]>([]);
  const [loading, setLoading] = useState(true);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskItemDto | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!projectId) return;
    try {
      const [projectData, statsData, membersData, tasksData] = await Promise.all([
        taskService.getProjectById(projectId),
        taskService.getProjectStatistics(projectId),
        taskService.getProjectMembers(projectId),
        taskService.getProjectTasks(projectId),
      ]);
      setProject(projectData);
      setStatistics(statsData);
      setMembers(membersData);
      setTasks(tasksData);
    } catch (error) {
      console.error('Error loading project:', error);
      notify('Error', 'Failed to load project data', 'danger');
    } finally {
      setLoading(false);
    }
  }, [projectId, notify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateTask = async (dto: CreateTaskItemDto) => {
    if (!projectId) return;
    const created = await taskService.createTask(projectId, dto);
    setTasks((prev) => [...prev, created]);
    notify('Created', 'Task created successfully', 'success');
    // Refresh statistics
    try {
      const stats = await taskService.getProjectStatistics(projectId);
      setStatistics(stats);
    } catch {
      // Ignore stats refresh error
    }
  };

  const handleTaskClick = (task: TaskItemDto) => {
    setSelectedTask(task);
    setDetailSheetOpen(true);
  };

  const handleTaskUpdated = (updatedTask: TaskItemDto) => {
    setTasks((prev) => prev.map((t) => (t.id === updatedTask.id ? updatedTask : t)));
    // Refresh statistics
    if (projectId) {
      taskService.getProjectStatistics(projectId)
        .then(setStatistics)
        .catch(() => {});
    }
  };

  const handleTaskDeleted = (taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    notify('Deleted', 'Task deleted successfully', 'success');
    // Refresh statistics
    if (projectId) {
      taskService.getProjectStatistics(projectId)
        .then(setStatistics)
        .catch(() => {});
    }
  };

  const handleTasksChange = (newTasks: TaskItemDto[]) => {
    setTasks(newTasks);
  };

  if (loading) return <LoadingSpinner label="Loading project..." />;

  if (!project) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent>
            <div className="flex flex-col items-center gap-2 text-center py-12">
              <p className="font-medium text-muted-foreground">Project not found</p>
              <Button variant="outline" onClick={() => navigate('/projects')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Projects
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <Card>
        <CardIconHeader icon={<FolderKanban size={22} className="text-primary" />}>
          <div className="flex items-center justify-between w-full">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/projects')}
                  className="h-7 w-7"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <CardTitle>{project.name}</CardTitle>
              </div>
              {project.description && (
                <CardDescription className="ml-9">
                  {project.description}
                </CardDescription>
              )}
            </div>
            <Button onClick={() => setCreateDialogOpen(true)} className="ml-4 shrink-0">
              <Plus className="mr-2 h-4 w-4" />
              Add Task
            </Button>
          </div>
        </CardIconHeader>
      </Card>

      {/* Statistics Bar */}
      {statistics && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 stagger-children">
          <Card className="p-3 animate-fade-in-up">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted">
                <ListTodo className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <div className="text-lg font-bold">{statistics.totalTasks}</div>
                <div className="text-[11px] text-muted-foreground">Total</div>
              </div>
            </div>
          </Card>
          <Card className="p-3 animate-fade-in-up">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-500/10">
                <div className="h-2 w-2 rounded-full bg-slate-400" />
              </div>
              <div>
                <div className="text-lg font-bold">{statistics.todoCount}</div>
                <div className="text-[11px] text-muted-foreground">To Do</div>
              </div>
            </div>
          </Card>
          <Card className="p-3 animate-fade-in-up">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-500/10">
                <Clock className="h-4 w-4 text-blue-500" />
              </div>
              <div>
                <div className="text-lg font-bold">{statistics.inProgressCount}</div>
                <div className="text-[11px] text-muted-foreground">In Progress</div>
              </div>
            </div>
          </Card>
          <Card className="p-3 animate-fade-in-up">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-500/10">
                <Eye className="h-4 w-4 text-amber-500" />
              </div>
              <div>
                <div className="text-lg font-bold">{statistics.inReviewCount}</div>
                <div className="text-[11px] text-muted-foreground">In Review</div>
              </div>
            </div>
          </Card>
          <Card className="p-3 animate-fade-in-up">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-500/10">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
              <div>
                <div className="text-lg font-bold">{statistics.doneCount}</div>
                <div className="text-[11px] text-muted-foreground">Done</div>
              </div>
            </div>
          </Card>
          <Card className="p-3 animate-fade-in-up">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-destructive/10">
                <AlertTriangle className="h-4 w-4 text-destructive" />
              </div>
              <div>
                <div className="text-lg font-bold">{statistics.overdueTasks}</div>
                <div className="text-[11px] text-muted-foreground">Overdue</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Completion Progress */}
      {statistics && statistics.totalTasks > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">Completion</span>
                <Badge variant={statistics.completionPercentage === 100 ? 'success' : 'secondary'}>
                  {statistics.completionPercentage}%
                </Badge>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-700 ease-out"
                  style={{ width: `${statistics.completionPercentage}%` }}
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Kanban Board */}
      <KanbanBoard
        tasks={tasks}
        onTasksChange={handleTasksChange}
        onTaskClick={handleTaskClick}
      />

      {/* Dialogs / Sheets */}
      <CreateTaskDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateTask}
        members={members}
      />

      <TaskDetailSheet
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        task={selectedTask}
        members={members}
        onTaskUpdated={handleTaskUpdated}
        onTaskDeleted={handleTaskDeleted}
      />
    </div>
  );
};

export default ProjectDetail;
